import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

function reportError(error: Error, info: ErrorInfo) {
  console.error('[ErrorBoundary]', error, info)
  try {
    const w = window as unknown as { __errorReporter?: (e: Error, i: ErrorInfo) => void }
    if (w.__errorReporter) w.__errorReporter(error, info)
  } catch {
    // reporter must never throw
  }
  try {
    localStorage.setItem(
      'spinex_admin_last_error',
      JSON.stringify({
        message: error.message,
        stack: error.stack,
        componentStack: info.componentStack,
        url: window.location.href,
        at: new Date().toISOString(),
      }),
    )
  } catch {
    // storage may be full or blocked
  }
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, info)
  }

  reset = () => this.setState({ hasError: false, error: undefined })

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.state.error, this.reset)
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg p-4">
          <div className="max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-red-400 mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground mb-1">{this.state.error.message}</p>
            <p className="text-xs text-muted/60 mb-4">The error has been recorded.</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.reset}
                className="px-4 py-2 bg-surface border border-border text-text rounded-lg text-sm hover:bg-elevated transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary-hover transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
